import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Download, Trash2, X, FileText, Image as ImageIcon, File } from "lucide-react";
import api, { API_BASE } from "@/api";

const DOC_TIPI = ["generico", "contratto", "permesso", "dvr", "pos", "preventivo", "assicurazione", "certificato", "altro"];
const FOTO_TAGS = ["prima", "durante", "dopo", "problema", "attrezzature", "sicurezza"];
const TIPO_STYLE = {
  messaggio: "border-slate-200",
  avviso: "border-yellow-400 bg-yellow-50",
  urgente: "border-red-500 bg-red-50",
};
const TIPO_BADGE = {
  messaggio: "bg-slate-100 text-slate-800 border-slate-200",
  avviso: "bg-yellow-100 text-yellow-800 border-yellow-200",
  urgente: "bg-red-100 text-red-800 border-red-200",
};

// Deduce il MIME type dalla dataURL base64 (es. "data:application/pdf;base64,...")
const _mimeOfDataUrl = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return "";
  const m = dataUrl.match(/^data:([^;]+);base64,/i);
  return m ? m[1].toLowerCase() : "";
};

// Converte una dataURL in Blob per apertura in nuova tab / download
const _dataUrlToBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(",");
  const mime = _mimeOfDataUrl(dataUrl) || "application/octet-stream";
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

// ------------------- Documenti ---------------------
export function DocumentiTab({ cantiereId }) {
  const qc = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => api.get("/auth/me").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const isAdmin = me?.role === "amministratore";
  const [form, setForm] = useState({ nome: "", tipo: "generico", scadenza: "", note: "", contenuto_base64: null, filename: "", riservato: false });
  const [viewer, setViewer] = useState(null); // { documento_id, nome, filename, contenuto_base64, mime }
  const { data: docs = [] } = useQuery({
    queryKey: ["documenti", cantiereId],
    queryFn: () => api.get(`/entita/documenti?cantiere_id=${cantiereId}`).then((r) => r.data),
  });
  const create = useMutation({
    mutationFn: (payload) => api.post("/entita/documenti", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Documento caricato");
      qc.invalidateQueries({ queryKey: ["documenti", cantiereId] });
      setForm({ nome: "", tipo: "generico", scadenza: "", note: "", contenuto_base64: null, filename: "", riservato: false });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const toggleRiservato = useMutation({
    mutationFn: ({ id, riservato }) =>
      api.patch(`/entita/documenti/${id}/riservato`, { riservato }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(data.riservato ? "Documento oscurato ai dipendenti" : "Documento reso visibile");
      qc.invalidateQueries({ queryKey: ["documenti", cantiereId] });
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore modifica visibilità documento"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/entita/documenti/${id}`),
    onSuccess: () => {
      toast.success("Documento eliminato");
      qc.invalidateQueries({ queryKey: ["documenti", cantiereId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore eliminazione"),
  });
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("File troppo grande (max 5MB)");
    const reader = new FileReader();
    reader.onload = () => setForm((x) => ({ ...x, contenuto_base64: reader.result, filename: f.name, nome: x.nome || f.name }));
    reader.readAsDataURL(f);
  };

  // Fetch del documento completo (con contenuto_base64) e apertura del viewer.
  // La lista principale esclude il campo `contenuto_base64` per performance,
  // quindi va richiamato l'endpoint dettaglio.
  const openDocumento = async (d, mode = "view") => {
    try {
      const { data } = await api.get(`/entita/documenti/${d.documento_id}`);
      if (!data?.contenuto_base64) {
        toast.error("File non disponibile per questo documento");
        return;
      }
      if (mode === "download") {
        const blob = _dataUrlToBlob(data.contenuto_base64);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename || d.nome || "documento";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
        return;
      }
      setViewer({
        documento_id: data.documento_id,
        nome: data.nome,
        filename: data.filename,
        contenuto_base64: data.contenuto_base64,
        mime: _mimeOfDataUrl(data.contenuto_base64),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile aprire il documento");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 p-5">
        <div className="overline mb-3">Nuovo documento</div>
        <div className="grid md:grid-cols-4 gap-3">
          <input placeholder="Nome documento" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm md:col-span-2" />
          <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm">
            {DOC_TIPI.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input type="date" value={form.scadenza} onChange={(e) => setForm({ ...form, scadenza: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <input type="file" onChange={onFile} className="text-xs md:col-span-3" />

          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={!!form.riservato}
              onChange={(e) => setForm({ ...form, riservato: e.target.checked })}
            />
            🔒 Riservato agli amministratori
          </label>

          <button
            onClick={() => form.contenuto_base64 && form.nome && create.mutate({ ...form, cantiere_id: cantiereId })}
            disabled={!form.contenuto_base64 || !form.nome}
            data-testid="btn-carica-documento"
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm shadow-brutal text-sm"
          >Carica</button>
        </div>
        {form.filename && (
          <div className="text-xs text-slate-500 mt-2">File selezionato: <b>{form.filename}</b></div>
        )}
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">File</th>
              <th className="text-left p-3">Scadenza</th>
              <th className="text-left p-3">Caricato da</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.documento_id} className="border-t border-slate-100" data-testid={`doc-row-${d.documento_id}`}>
                <td className="p-3 font-medium">
                  {d.nome}
                  {d.riservato && (
                    <span className="ml-2 inline-block px-2 py-0.5 bg-rose-100 border border-rose-300 text-rose-700 rounded-sm text-[10px] font-bold">
                      🔒 RISERVATO
                    </span>
                  )}
                </td>
                <td className="p-3 text-xs">
                  <span className="inline-block px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm uppercase font-bold text-slate-700 text-[10px]">
                    {d.tipo}
                  </span>
                </td>
                <td className="p-3 text-xs text-slate-500 truncate max-w-[160px]" title={d.filename || ""}>
                  {d.filename || "—"}
                </td>
                <td className="p-3 text-xs font-mono">{d.scadenza || "—"}</td>
                <td className="p-3 text-xs">{d.uploaded_by}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => openDocumento(d, "view")}
                      data-testid={`btn-visualizza-doc-${d.documento_id}`}
                      title="Visualizza documento"
                      className="p-1.5 rounded-sm border border-blue-300 hover:bg-blue-50 text-blue-700 inline-flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openDocumento(d, "download")}
                      data-testid={`btn-scarica-doc-${d.documento_id}`}
                      title="Scarica documento"
                      className="p-1.5 rounded-sm border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() =>
                          toggleRiservato.mutate({
                            id: d.documento_id,
                            riservato: !d.riservato,
                          })
                        }
                        disabled={toggleRiservato.isPending}
                        title={d.riservato ? "Rendi visibile ai dipendenti" : "Oscura ai dipendenti"}
                        className={`p-1.5 rounded-sm border inline-flex items-center gap-1 ${
                          d.riservato
                            ? "border-rose-400 bg-rose-50 text-rose-700"
                            : "border-amber-300 hover:bg-amber-50 text-amber-700"
                        }`}
                      >
                        <span className="text-sm">
                          {d.riservato ? "🔒" : "🔓"}
                        </span>
                      </button>
                    )}

                    <button
                      onClick={() => {
                        if (window.confirm(`Eliminare definitivamente il documento "${d.nome}"?`))
                          del.mutate(d.documento_id);
                      }}
                      data-testid={`btn-elimina-doc-${d.documento_id}`}
                      title="Elimina documento"
                      className="p-1.5 rounded-sm border border-rose-300 hover:bg-rose-50 text-rose-700 inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-400">Nessun documento caricato</td></tr>}
          </tbody>
        </table>
      </div>

      {viewer && <DocumentoViewerModal doc={viewer} onClose={() => setViewer(null)} onDownload={() => {
        const blob = _dataUrlToBlob(viewer.contenuto_base64);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = viewer.filename || viewer.nome || "documento";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
      }} />}
    </div>
  );
}

function DocumentoViewerModal({ doc, onClose, onDownload }) {
  const isImage = doc.mime.startsWith("image/");
  const isPdf = doc.mime === "application/pdf";
  const isText = doc.mime.startsWith("text/") || ["application/json", "application/xml"].includes(doc.mime);

  const previewUrl = `${API_BASE}/entita/documenti/${doc.documento_id}/file`;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-documento-viewer"
    >
      <div
        className="bg-white shadow-brutal border border-slate-300 max-w-5xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {isImage ? <ImageIcon className="w-5 h-5 text-blue-700 shrink-0" /> :
             isPdf ? <FileText className="w-5 h-5 text-rose-700 shrink-0" /> :
             <File className="w-5 h-5 text-slate-600 shrink-0" />}
            <div className="min-w-0">
              <div className="overline text-slate-500">Documento</div>
              <div className="font-display font-bold text-base truncate">{doc.nome}</div>
              {doc.filename && <div className="text-xs text-slate-500 truncate">{doc.filename} · {doc.mime || "sconosciuto"}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onDownload}
              data-testid="btn-scarica-doc-viewer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-widest rounded-sm"
            >
              <Download className="w-3.5 h-3.5" /> Scarica
            </button>
            <button
              onClick={onClose}
              data-testid="btn-chiudi-doc-viewer"
              className="p-1.5 hover:bg-slate-200 rounded-sm"
              title="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100">
          {isImage && (
            <img src={previewUrl} alt={doc.nome} className="w-full h-auto" />
          )}
          {isPdf && (
            <iframe
              src={doc.contenuto_base64}
              title={`Anteprima PDF - ${doc.nome}`}
              className="w-full h-[70vh] border-0 bg-white"
            />
          )}
          {isText && (
            <TextDocPreview dataUrl={doc.contenuto_base64} />
          )}
          {!isImage && !isPdf && !isText && (
            <div className="p-10 text-center text-slate-600">
              <File className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <div className="font-semibold mb-2">Anteprima non disponibile per questo tipo di file</div>
              <div className="text-xs text-slate-500 mb-4">{doc.mime || "tipo sconosciuto"}</div>
              <button
                onClick={onDownload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-sm"
              >
                <Download className="w-4 h-4" /> Scarica il file per aprirlo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TextDocPreview({ dataUrl }) {
  const [text, setText] = React.useState("");
  React.useEffect(() => {
    try {
      const b64 = dataUrl.split(",")[1];
      setText(atob(b64));
    } catch {
      setText("Impossibile decodificare il testo del documento.");
    }
  }, [dataUrl]);
  return (
    <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words bg-white h-full overflow-auto">
      {text}
    </pre>
  );
}

// ------------------- Fotografie ---------------------
export function FotografieTab({ cantiereId }) {
  const qc = useQueryClient();

  const [didascalia, setDidascalia] = useState("");
  const [tag, setTag] = useState("durante");

  const [fotoSelezionata, setFotoSelezionata] = useState("");
  const [fotoNome, setFotoNome] = useState("");
  const [fotoViewer, setFotoViewer] = useState(null);

  const { data: fotos = [] } = useQuery({
    queryKey: ["fotografie", cantiereId],
    queryFn: () =>
      api.get(`/entita/fotografie?cantiere_id=${cantiereId}`).then((r) => r.data),
  });

  const upload = useMutation({
    mutationFn: (payload) =>
      api.post("/entita/fotografie", payload).then((r) => r.data),

    onSuccess: () => {
      toast.success("Foto caricata");
      qc.invalidateQueries({ queryKey: ["fotografie", cantiereId] });

      setDidascalia("");
      setFotoSelezionata("");
      setFotoNome("");
    },

    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore caricamento foto"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/entita/fotografie/${id}`),

    onSuccess: () => {
      toast.success("Foto eliminata");
      qc.invalidateQueries({ queryKey: ["fotografie", cantiereId] });
    },

    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore eliminazione foto"),
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];

    if (!f) return;

    if (!f.type.startsWith("image/")) {
      toast.error("Seleziona una fotografia");
      return;
    }

    if (f.size > 5 * 1024 * 1024) {
      toast.error("Foto troppo grande (max 5MB)");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setFotoSelezionata(String(reader.result || ""));
      setFotoNome(f.name);
    };

    reader.readAsDataURL(f);
  };

  const caricaFoto = () => {
    if (!fotoSelezionata) {
      toast.error("Seleziona prima una fotografia");
      return;
    }

    upload.mutate({
      cantiere_id: cantiereId,
      foto_base64: fotoSelezionata,
      didascalia,
      tag,
    });
  };

  return (
    <div className="space-y-4">

      <div className="bg-white border border-slate-200 p-5">
        <div className="overline mb-3">Nuova fotografia</div>

        <div className="grid md:grid-cols-3 gap-3">

          <input
            placeholder="Didascalia"
            value={didascalia}
            onChange={(e) => setDidascalia(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />

          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
          >
            {FOTO_TAGS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="text-xs"
          />
        </div>

        {fotoSelezionata && (
          <div className="mt-4 border border-slate-300 bg-slate-50 p-4">

            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Anteprima
            </div>

            <div className="bg-white border border-slate-200 p-2">
              <img
                src={fotoSelezionata}
                alt="Anteprima fotografia"
                className="max-w-full max-h-[420px] mx-auto object-contain"
              />
            </div>

            <div className="text-xs text-slate-500 mt-2">
              {fotoNome}
            </div>

            <div className="flex gap-2 mt-3">

              <button
                type="button"
                onClick={caricaFoto}
                disabled={upload.isPending}
                className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-sm disabled:opacity-50"
              >
                {upload.isPending ? "Caricamento..." : "Carica foto"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setFotoSelezionata("");
                  setFotoNome("");
                }}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-sm font-semibold rounded-sm"
              >
                Annulla
              </button>

            </div>
          </div>
        )}
      </div>


      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">

        {fotos.map((f) => (

          <div
            key={f.foto_id}
            className="bg-white border border-slate-200 overflow-hidden group relative"
          >

            <button
              type="button"
              onClick={() => setFotoViewer(f)}
              className="block w-full cursor-zoom-in"
              title="Apri anteprima"
            >
              <img
                src={f.foto_base64}
                alt={f.didascalia || "Fotografia"}
                className="w-full h-40 object-cover"
              />
            </button>

            <div className="p-2">

              <div className="text-xs font-medium truncate">
                {f.didascalia || "—"}
              </div>

              <div className="text-[10px] text-slate-500 flex items-center justify-between">

                <span className="uppercase tracking-wider">
                  {f.tag}
                </span>

                <span>
                  {f.created_at
                    ? new Date(f.created_at).toLocaleDateString("it-IT")
                    : ""}
                </span>

              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();

                if (window.confirm("Eliminare questa fotografia?")) {
                  del.mutate(f.foto_id);
                }
              }}
              className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Elimina"
            >
              ✕
            </button>

          </div>
        ))}

        {fotos.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-10">
            Nessuna foto caricata
          </div>
        )}
      </div>


      {fotoViewer && (

        <div
          className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setFotoViewer(null)}
        >

          <div
            className="relative max-w-6xl w-full max-h-[95vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              type="button"
              onClick={() => setFotoViewer(null)}
              className="absolute top-2 right-2 z-20 bg-white text-black p-2 rounded-sm shadow"
              title="Chiudi anteprima"
            >
              <X className="w-5 h-5" />
            </button>

            <img
              src={fotoViewer.foto_base64}
              alt={fotoViewer.didascalia || "Fotografia"}
              className="max-w-full max-h-[85vh] object-contain bg-black"
            />

            <div className="bg-white w-full px-4 py-3">

              <div className="font-semibold">
                {fotoViewer.didascalia || "Fotografia"}
              </div>

              <div className="text-xs uppercase text-slate-500">
                {fotoViewer.tag || ""}
              </div>

            </div>

          </div>
        </div>
      )}

    </div>
  );
}

// ------------------- Comunicazioni operative ---------------------
export function ComunicazioniTab({ cantiereId }) {
  const qc = useQueryClient();
  const [testo, setTesto] = useState("");
  const [tipo, setTipo] = useState("messaggio");
  const { data: msgs = [] } = useQuery({
    queryKey: ["comunicazioni", cantiereId],
    queryFn: () => api.get(`/entita/comunicazioni?cantiere_id=${cantiereId}`).then((r) => r.data),
  });
  const send = useMutation({
    mutationFn: (payload) => api.post("/entita/comunicazioni", payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comunicazioni", cantiereId] });
      setTesto("");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/entita/comunicazioni/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["comunicazioni", cantiereId] }),
  });

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 p-5">
        <div className="overline mb-3">Nuova comunicazione</div>
        <div className="grid md:grid-cols-4 gap-3">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm">
            <option value="messaggio">Messaggio</option>
            <option value="avviso">Avviso</option>
            <option value="urgente">URGENTE</option>
          </select>
          <textarea placeholder="Testo comunicazione..." value={testo} onChange={(e) => setTesto(e.target.value)} rows={2}
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm md:col-span-2" />
          <button
            onClick={() => testo.trim() && send.mutate({ cantiere_id: cantiereId, testo: testo.trim(), tipo })}
            disabled={!testo.trim()}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm shadow-brutal text-sm"
          >Pubblica</button>
        </div>
      </div>
      <div className="space-y-2">
        {msgs.map((m) => (
          <div key={m.comunicazione_id} className={`bg-white border-l-4 border p-4 ${TIPO_STYLE[m.tipo] || ""}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 border rounded-sm ${TIPO_BADGE[m.tipo]}`}>{m.tipo}</span>
                <span className="text-sm font-medium">{m.autore_nome}</span>
                <span className="text-xs text-slate-500">{m.autore_ruolo?.replace(/_/g, " ")}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-mono">{new Date(m.created_at).toLocaleString("it-IT")}</span>
                <button onClick={() => del.mutate(m.comunicazione_id)} className="text-red-600 hover:text-red-800 text-xs">✕</button>
              </div>
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{m.testo}</div>
          </div>
        ))}
        {msgs.length === 0 && <div className="text-center text-slate-400 py-10 border border-dashed border-slate-200 bg-white">Nessuna comunicazione</div>}
      </div>
    </div>
  );
}
